import InputError from "@/Components/InputError";
import MainButton from "@/Components/MainButton";
import PasswordInput from "@/Components/PasswordInput";
import SelectInput from "@/Components/SelectInput";
import TextInput from "@/Components/TextInput";
import GuestLayout from "@/Layouts/GuestLayout";
import { SelectInputProps } from "@/types/components/SelectInputProps";
import { DoctorRegisterData, RegistrationRole } from "@/types/registration/DoctorRegisterData";
import { DoctorRegisterViewProps } from "@/types/registration/DoctorRegisterViewProps";
import { Transition } from "@headlessui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Head, Link, useForm } from "@inertiajs/react";
import {
    FormEventHandler,
    Fragment,
    useEffect,
    useMemo,
    useState,
} from "react";
import { ValidationError, boolean, object, ref, string } from "yup";

const buildValidationSchema = (role: RegistrationRole) =>
    object({
        role: string().oneOf(["doctor", "pharmacist"]).required(),
        email: string()
            .required("Wprowadzono nieprawidłowy adres e-mail.")
            .email("Wprowadzono nieprawidłowy adres e-mail."),
        phone: string()
            .nullable()
            .test(
                "nine-digits",
                "Wprowadzono nieprawidłowy numer telefonu.",
                (value) => {
                    if (!value || value.trim() === "") return true;
                    const digitsOnly = value.replace(/\s/g, "");
                    return /^\d{9}$/.test(digitsOnly);
                },
            ),
        marketing_acceptance: boolean(),
        terms_of_use: boolean().oneOf(
            [true],
            "Musisz zaakceptować regulamin i politykę prywatności.",
        ),
        name: string()
            .required("Wprowadzono nieprawidłowe imię.")
            .matches(/^[\p{L} ]+$/u, "Wprowadzono nieprawidłowe imię."),
        surname: string()
            .required("Wprowadzono nieprawidłowe nazwisko.")
            .matches(
                /^\p{L}+(?:[\s-]+\p{L}+)*$/u,
                "Wprowadzono nieprawidłowe nazwisko.",
            ),
        date_of_birth:
            role === "pharmacist"
                ? string().nullable()
                : string()
                    .required("Wprowadzono nieprawidłową datę urodzenia.")
                    .matches(
                        /^(19|20)\d{2}-\d{2}-\d{2}$/,
                        "Wprowadzono nieprawidłową datę urodzenia.",
                    ),
        pwz:
            role === "pharmacist"
                ? string()
                    .required("Wprowadzono nieprawidłowy numer PWZ.")
                    .matches(
                        /^\d{7}$/,
                        "Wprowadzono nieprawidłowy numer PWZ.",
                    )
                : string()
                    .required("Wprowadzono nieprawidłowy numer PWZ.")
                    .min(6, "Wprowadzono nieprawidłowy numer PWZ.")
                    .max(7, "Wprowadzono nieprawidłowy numer PWZ.")
                    .matches(/^\d+$/, "Wprowadzono nieprawidłowy numer PWZ."),
        specialization_id:
            role === "pharmacist"
                ? string().nullable()
                : string()
                    .required("Wybierz specjalizację.")
                    .notOneOf(["0"], "Wybierz specjalizację."),
        pharmacist_declaration:
            role === "pharmacist"
                ? boolean().oneOf(
                    [true],
                    "Musisz potwierdzić posiadanie uprawnień do wykonywania zawodu farmaceuty.",
                )
                : boolean(),
        password: string()
            .required("Hasło musi posiadać przynajmniej 8 znaków.")
            .min(8, "Hasło musi posiadać przynajmniej 8 znaków.")
            .matches(/\d/, "Hasło musi zawierać co najmniej jedną cyfrę.")
            .matches(
                /\W|_/,
                "Hasło musi zawierać co najmniej jeden znak specjalny.",
            )
            .matches(
                /[A-Z]/,
                "Hasło musi zawierać co najmniej jedną wielką literę.",
            )
            .matches(/[a-z]/, "Hasło musi zawierać co najmniej jedną małą literę."),
        password_confirmation: string()
            .required("Hasło zostało poprawnie powtórzone.")
            .oneOf([ref("password")], "Hasła muszą być identyczne."),
    });

type ClientErrorState = Partial<Record<keyof DoctorRegisterData, string>>;

const initialClientErrors: ClientErrorState = {
    email: "",
    phone: "",
    marketing_acceptance: "",
    terms_of_use: "",
    name: "",
    surname: "",
    date_of_birth: "",
    pwz: "",
    specialization_id: "",
    password: "",
    password_confirmation: "",
    role: "",
    pharmacist_declaration: "",
};

const Register = ({
    specializations = [],
    marketingConsents = {},
}: DoctorRegisterViewProps) => {
    const [clientErrors, setClientErrors] =
        useState<ClientErrorState>(initialClientErrors);
    const [isMarketingConsentExpanded, setIsMarketingConsentExpanded] =
        useState<boolean>(false);
    const [selectedRole, setSelectedRole] =
        useState<RegistrationRole>("doctor");

    const { data, setData, post, processing, errors, transform } =
        useForm<DoctorRegisterData>({
            email: "",
            phone: "",
            marketing_acceptance: false,
            terms_of_use: false,
            name: "",
            surname: "",
            date_of_birth: "",
            pwz: "",
            specialization_id: "0",
            password: "",
            password_confirmation: "",
            role: "doctor",
            pharmacist_declaration: false,
        });

    const isPharmacist = selectedRole === "pharmacist";
    const activeMarketingConsent =
        marketingConsents[isPharmacist ? "pharmacist" : "doctor"] ?? null;

    const selectRole = (role: RegistrationRole) => {
        setSelectedRole(role);
    };

    useEffect(() => {
        setData((previous) => {
            if (previous.role === selectedRole) {
                return previous;
            }

            return {
                ...previous,
                role: selectedRole,
                pharmacist_declaration: false,
                date_of_birth:
                    selectedRole === "pharmacist"
                        ? ""
                        : previous.date_of_birth,
                specialization_id:
                    selectedRole === "pharmacist"
                        ? "0"
                        : previous.specialization_id,
            };
        });
    }, [selectedRole, setData]);

    const specializationOptions: SelectInputProps[] = useMemo(
        () =>
            specializations.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [specializations],
    );

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        const formData = { ...data, role: selectedRole };

        buildValidationSchema(selectedRole)
            .validate(formData, { abortEarly: false })
            .then(() => {
                setClientErrors(initialClientErrors);
                transform((previous) => ({
                    ...previous,
                    role: selectedRole,
                    date_of_birth:
                        selectedRole === "pharmacist"
                            ? ""
                            : previous.date_of_birth,
                    specialization_id:
                        selectedRole === "pharmacist"
                            ? "0"
                            : previous.specialization_id,
                }));
                post(route("register"), {
                    preserveScroll: true,
                });
            })
            .catch((validationError) => {
                if (validationError instanceof ValidationError) {
                    const newErrors = { ...initialClientErrors };
                    validationError.inner.forEach((err) => {
                        const field = err.path as
                            | keyof DoctorRegisterData
                            | undefined;
                        if (field && !newErrors[field]) {
                            newErrors[field] = err.message;
                        }
                    });
                    setClientErrors(newErrors);
                    setTimeout(() => {
                        scrollToFirstError();
                    }, 100);
                }
            });
    };

    const showError = (field: keyof DoctorRegisterData) =>
        clientErrors[field] || errors[field];

    const scrollToFirstError = () => {
        const fieldOrder: (keyof DoctorRegisterData)[] = [
            "email",
            "phone",
            "password",
            "password_confirmation",
            "name",
            "surname",
            "date_of_birth",
            "pwz",
            "specialization_id",
            "pharmacist_declaration",
            "terms_of_use",
            "marketing_acceptance",
        ];

        for (const field of fieldOrder) {
            const hasError = clientErrors[field] || errors[field];
            if (hasError) {
                const element = document.getElementById(field);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    element.focus();
                    break;
                }
            }
        }
    };

    useEffect(() => {
        const allErrors = { ...clientErrors, ...errors };
        if (Object.keys(allErrors).length > 0) {
            setTimeout(() => {
                scrollToFirstError();
            }, 100);
        }
    }, [errors, clientErrors]);

    const passwordLength = data.password.length;
    const passwordSpecialChar = /\W|_/.test(data.password);
    const passwordHasNumber = /\d/.test(data.password);
    const passwordHasBigLetter = /[A-Z]/.test(data.password);
    const passwordHasSmallLetter = /[a-z]/.test(data.password);
    const passwordConfirmationSame =
        data.password !== "" &&
        data.password_confirmation !== "" &&
        data.password === data.password_confirmation;

    return (
        <GuestLayout>
            <Head title="Rejestracja" />
            <p className="text-arcticMist text-center text-h4 leading-h4 font-semibold tracking-[0.48px] mb-6">
                Posiadasz już konto?
            </p>
            <Link
                title="Zaloguj się"
                href={route("login")}
                className="main-btn w-full block mb-10"
            >
                Zaloguj się
            </Link>

            <hr className="border-royalPeriwinkle mb-10" />

            <form onSubmit={submit} noValidate>
                <p className="text-accordorange text-base font-semibold tracking-[0.24px] mb-1">
                    Rejestracja
                </p>
                <h1 className="text-arcticMist text-h4 leading-h4 font-semibold tracking-[0.48px] mb-6">
                    Załóż konto
                </h1>

                <div
                    className="grid grid-cols-2 gap-3 mb-11"
                    role="radiogroup"
                    aria-label="Typ konta"
                >
                    <div className="flex items-center gap-x-6 py-1 relative">
                        <input
                            type="radio"
                            name="role"
                            id="role_doctor"
                            className="hidden registration-radio-input"
                            value="doctor"
                            checked={selectedRole === "doctor"}
                            onChange={() => selectRole("doctor")}
                        />
                        <label
                            htmlFor="role_doctor"
                            className="registration-radio-label"
                        >
                            Jestem lekarzem
                        </label>
                    </div>
                    <div className="flex items-center gap-x-6 py-1 relative">
                        <input
                            type="radio"
                            name="role"
                            id="role_pharmacist"
                            className="hidden registration-radio-input"
                            value="pharmacist"
                            checked={selectedRole === "pharmacist"}
                            onChange={() => selectRole("pharmacist")}
                        />
                        <label
                            htmlFor="role_pharmacist"
                            className="registration-radio-label"
                        >
                            Jestem farmaceutą
                        </label>
                    </div>
                </div>

                <div className="mb-7">
                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="w-full"
                        autoComplete="username"
                        isFocused
                        placeholder="E-mail"
                        icon="ic:baseline-mode-edit-outline"
                        required
                        contextInfo="Podanego adresu będziesz używać do logowania się do serwisu Accord Academy."
                        hasError={showError("email")}
                        onChange={(e) => setData("email", e.target.value)}
                    />
                    <InputError message={showError("email")} />
                    <p className="text-arcticMist text-12 leading-4 tracking-[0.18px] mt-1 maxMd:pr-2 pl-1">
                        Na ten adres otrzymasz wszystkie wiadomości niezbędne do
                        zarządzania swoim profilem Accord Academy.
                    </p>
                </div>

                <div className="mb-7 relative">
                    <TextInput
                        id="phone"
                        type="text"
                        name="phone"
                        value={data.phone}
                        className={`w-full ${data.phone.length > 0 ? "pl-12" : "pl-4"}`}
                        autoComplete="tel"
                        placeholder="Telefon"
                        icon="ic:baseline-mode-edit-outline"
                        hasError={showError("phone")}
                        onChange={(e) => {
                            const rawValue = e.target.value;
                            const sanitized = rawValue.replace(/[^\d\s]/g, "");
                            const digitsOnly = sanitized.replace(/\s/g, "");

                            if (digitsOnly.length <= 9) {
                                setData("phone", sanitized);
                            }
                        }}
                    />
                    <p
                        className={`text-arcticMist/75 text-base absolute transition-all ease-in-out duration-300 left-3 ${data.phone.length > 0 ? "top-[23.5px]" : "opacity-0"}`}
                    >
                        +48
                    </p>
                    <InputError message={showError("phone")} />
                    <p className="text-arcticMist text-12 leading-4 tracking-[0.18px] mt-1 maxMd:pr-2 pl-1">
                        Numer jest opcjonalny. Zostanie wykorzystany do informowania Państwa o
                        nowych materiałach zamieszczonych na platformie (jeżeli wyrażono zgodę).
                    </p>
                </div>

                <div className="mb-7">
                    <PasswordInput
                        id="password"
                        name="password"
                        placeholder="Hasło"
                        value={data.password}
                        className="w-full"
                        required
                        hasError={showError("password")}
                        autoComplete="new-password"
                        onChange={(e) => setData("password", e.target.value)}
                    />
                    <div className="indicator-length text-right mt-1 maxMd:max-w-[calc(100%_-_16px)]">
                        <span
                            className={`${passwordLength < 8
                                ? "text-crimsonAlert"
                                : "text-emeraldPulse"
                                }`}
                        >
                            {passwordLength < 10
                                ? `0${passwordLength}`
                                : passwordLength}
                        </span>
                        <span className="text-emeraldPulse mx-2">|</span>
                        <span className="text-[#1B9C28]">08</span>
                    </div>
                    <InputError message={showError("password")} />
                </div>

                <div className="password-milestones mb-7">
                    <div
                        className={`flex items-center gap-x-4 ${passwordSpecialChar
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } mb-4 transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło posiada jeden znak specjalny</p>
                    </div>
                    <div
                        className={`flex items-center gap-x-4 ${passwordHasNumber
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } mb-4 transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło posiada jedną cyfrę</p>
                    </div>
                    <div
                        className={`flex items-center gap-x-4 ${passwordLength >= 8
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } mb-4 transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło posiada odpowiednią ilość znaków</p>
                    </div>
                    <div
                        className={`flex items-center gap-x-4 ${passwordHasBigLetter
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } mb-4 transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło posiada przynajmniej jedną wielką literę</p>
                    </div>
                    <div
                        className={`flex items-center gap-x-4 ${passwordHasSmallLetter
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } mb-4 transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło posiada przynajmniej jedną małą literę</p>
                    </div>
                    <div
                        className={`flex items-center gap-x-4 ${passwordConfirmationSame
                            ? "text-emeraldPulse"
                            : "text-slateGray"
                            } transition-theme`}
                    >
                        <Icon
                            icon={`ph:check-circle-fill`}
                            width={24}
                            height={24}
                        />
                        <p>Hasło zostało poprawnie powtórzone</p>
                    </div>
                </div>

                <div>
                    <PasswordInput
                        id="password_confirmation"
                        name="password_confirmation"
                        placeholder="Powtórz hasło"
                        value={data.password_confirmation}
                        className="w-full"
                        hasError={showError("password_confirmation")}
                        autoComplete="new-password"
                        required
                        onChange={(e) =>
                            setData("password_confirmation", e.target.value)
                        }
                    />
                    <InputError message={showError("password_confirmation")} />
                    <p className="text-arcticMist text-12 leading-4 tracking-[0.18px] mt-1 maxMd:pr-2 pl-1">
                        Prosimy Cię o powtórzenie hasła aby zminimalizować
                        ewentualne pomyłki podczas jego wprowadzania.
                    </p>
                </div>

                <hr className="border-royalPeriwinkle my-7" />

                <div className="mb-7">
                    <div>
                        <TextInput
                            id="name"
                            type="text"
                            name="name"
                            value={data.name}
                            className="w-full"
                            autoComplete="given-name"
                            placeholder="Imię"
                            icon="ic:baseline-mode-edit-outline"
                            required
                            hasError={showError("name")}
                            onChange={(e) => setData("name", e.target.value)}
                        />
                        <InputError message={showError("name")} />
                    </div>
                </div>

                <div>
                    <TextInput
                        id="surname"
                        type="text"
                        name="surname"
                        value={data.surname}
                        className="w-full"
                        autoComplete="family-name"
                        placeholder="Nazwisko"
                        icon="ic:baseline-mode-edit-outline"
                        required
                        hasError={showError("surname")}
                        onChange={(e) => setData("surname", e.target.value)}
                    />
                    <InputError message={showError("surname")} />
                </div>

                <hr className="border-royalPeriwinkle my-7" />

                {!isPharmacist && (
                    <div className="mb-7">
                        <TextInput
                            id="date_of_birth"
                            type="date"
                            name="date_of_birth"
                            value={data.date_of_birth}
                            className="w-full"
                            autoComplete="bday"
                            placeholder="Data urodzenia"
                            icon="tabler:calendar"
                            required
                            max={new Date().toISOString().split("T")[0]}
                            min={"1900-01-01"}
                            hasError={showError("date_of_birth")}
                            onChange={(e) =>
                                setData("date_of_birth", e.target.value)
                            }
                        />
                        <InputError message={showError("date_of_birth")} />
                        <p className="text-arcticMist text-12 leading-4 tracking-[0.18px] mt-1 maxMd:pr-2 pl-1">
                            Data urodzenia jest niezbędna podczas procesu
                            weryfikacji w Naczelnej Izbie Lekarskiej
                        </p>
                    </div>
                )}

                <div className="mb-7">
                    <TextInput
                        id="pwz"
                        type="text"
                        name="pwz"
                        value={data.pwz}
                        className="w-full"
                        placeholder="Numer PWZ"
                        icon="ic:baseline-mode-edit-outline"
                        required
                        hasError={showError("pwz")}
                        onChange={(e) => setData("pwz", e.target.value)}
                    />
                    <InputError message={showError("pwz")} />
                    {!isPharmacist && (
                        <p className="text-arcticMist text-12 leading-4 tracking-[0.18px] mt-1 maxMd:pr-2 pl-1">
                            Numer PWZ pozwala na weryfikację przez Naczelną Izbę
                            Lekarską
                        </p>
                    )}
                </div>

                {!isPharmacist && (
                    <div
                        id="specialization_id"
                        className="maxMd:max-w-[calc(100%_-_16px)]"
                    >
                        <SelectInput
                            options={specializationOptions}
                            setData={setData}
                            label="Specjalizacja wykonywana"
                            requiredMark
                            defaultValue={data.specialization_id}
                            labelClass="text-arcticMist text-14"
                            defaultLabel={
                                specializationOptions.find(
                                    (option) =>
                                        option.value === data.specialization_id,
                                )?.label ?? ""
                            }
                        />
                        <InputError message={showError("specialization_id")} />
                    </div>
                )}

                <div className="relative pl-7 my-7">
                    <input
                        type="checkbox"
                        className="hidden marketing_acceptance"
                        id="terms_of_use"
                        checked={data.terms_of_use}
                        onChange={(e) =>
                            setData("terms_of_use", e.currentTarget.checked)
                        }
                    />
                    <label
                        className="text-arcticMist text-14 leading-[18px] tracking-[0.21px] label-input-registration cursor-pointer"
                        htmlFor="terms_of_use"
                    >

                        (Pole wymagane) Potwierdzam zapoznanie się z  <Link
                            href={route("front.terms")}
                            className="text-accordorange underline"
                        >
                            regulaminem
                        </Link> i <Link
                            href={route("front.privacyPolicy")}
                            className="text-accordorange underline"
                        >
                            polityką prywatności
                        </Link> Accord Academy i akceptuję ich treść.



                    </label>
                    <InputError message={showError("terms_of_use")} />
                </div>

                {isPharmacist && (
                    <div className="relative pl-7 mb-7">
                        <input
                            type="checkbox"
                            className="hidden marketing_acceptance"
                            id="pharmacist_declaration"
                            checked={data.pharmacist_declaration}
                            onChange={(e) =>
                                setData(
                                    "pharmacist_declaration",
                                    e.currentTarget.checked,
                                )
                            }
                        />
                        <label
                            className="text-arcticMist text-14 leading-[18px] tracking-[0.21px] label-input-registration cursor-pointer"
                            htmlFor="pharmacist_declaration"
                        >
                            (Pole wymagane) Oświadczam, że posiadam uprawnienia do wykonywania zawodu farmaceuty zgodnie z obowiązującymi przepisami prawa oraz że jestem świadomy(-a) odpowiedzialności za podanie nieprawdziwych danych.
                        </label>
                        <InputError message={showError("pharmacist_declaration")} />
                    </div>
                )}



                <hr className="border-royalPeriwinkle my-7" />

                <div className="flex items-start relative mb-8">
                    <input
                        type="checkbox"
                        className="hidden marketing_acceptance"
                        id="marketing_acceptance"
                        checked={data.marketing_acceptance}
                        onChange={(e) =>
                            setData(
                                "marketing_acceptance",
                                e.currentTarget.checked,
                            )
                        }
                    />
                    <label
                        className="text-arcticMist text-14 leading-[18px] tracking-[0.21px] label-input-registration ml-[30px] cursor-pointer"
                        htmlFor="marketing_acceptance"
                    >
                        <div className="mb-2 inline-block space-y-4">
                            <p>
                                {activeMarketingConsent?.summary ??
                                    "Wyrażam zgodę na przetwarzanie moich danych osobowych w celach marketingowych."}
                            </p>

                            <Transition
                                as={Fragment}
                                show={isMarketingConsentExpanded}
                                enter="transition-all duration-300 ease-out"
                                enterFrom="opacity-0 max-h-0"
                                enterTo="opacity-100 max-h-[2000px]"
                                leave="transition-all duration-300 ease-in"
                                leaveFrom="opacity-100 max-h-[2000px]"
                                leaveTo="opacity-0 max-h-0"
                            >
                                <div
                                    className="overflow-hidden space-y-4 [&_a]:text-accordorange [&_a]:underline"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            activeMarketingConsent?.content ??
                                            "",
                                    }}
                                />
                            </Transition>
                        </div>
                    </label>
                    <InputError message={showError("marketing_acceptance")} />
                    <button
                        type="button"
                        onClick={() =>
                            setIsMarketingConsentExpanded(
                                !isMarketingConsentExpanded,
                            )
                        }
                        className="text-accordorange flex gap-x-2 py-1 pl-4 text-12 leading-4 tracking-[0.18px] items-center"
                    >
                        {isMarketingConsentExpanded ? "zwiń" : "rozwiń"}
                        <Icon
                            icon={`tabler:chevron-up`}
                            width={16}
                            className={`transition-transform duration-200 ${isMarketingConsentExpanded
                                ? "rotate-0"
                                : "rotate-180"
                                }`}
                        />
                    </button>
                </div>

                <MainButton
                    type="submit"
                    title="Zarejestruj się"
                    disabled={processing}
                    className="mb-10"
                >
                    {processing ? "Przetwarzanie..." : "Zarejestruj się"}
                </MainButton>
            </form>
            <hr className="border-royalPeriwinkle mb-7" />
            <a
                href={route("oauth.google")}
                title="Zarejestruj się przez Google"
                className="outline-btn flex gap-x-2 items-center justify-center mb-7"
            >
                <Icon
                    icon={`ri:google-fill`}
                    className="text-arcticMist mr-2"
                    width={24}
                    height={24}
                />
                Zarejestruj się przez Google
            </a>
        </GuestLayout>
    );
};

export default Register;
